import { useState, useEffect, useRef } from "react";
import { getComparePage } from "../../api/compare";

/**
 * Hook that fetches and caches full-resolution images for both drawings.
 * Returns { latestImage, referenceImage, loading, error }
 */
export default function useComparisonImages(sessionId, pageNum = 1) {
  const [latestImage, setLatestImage] = useState(null);
  const [referenceImage, setReferenceImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cacheRef = useRef({});

  useEffect(() => {
    if (!sessionId) return;

    const cacheKey = `${sessionId}_${pageNum}`;
    if (cacheRef.current[cacheKey]) {
      const cached = cacheRef.current[cacheKey];
      setLatestImage(cached.latest);
      setReferenceImage(cached.reference);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      getComparePage(sessionId, "latest", pageNum),
      getComparePage(sessionId, "reference", pageNum),
    ])
      .then(([latestRes, refRes]) => {
        if (cancelled) return;
        const latest = latestRes.data.image_base64 || latestRes.data.image || null;
        const reference = refRes.data.image_base64 || refRes.data.image || null;
        cacheRef.current[cacheKey] = { latest, reference };
        setLatestImage(latest);
        setReferenceImage(reference);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.message || "Failed to load images");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [sessionId, pageNum]);

  return { latestImage, referenceImage, loading, error };
}
